/**
 * DemoNarrationService – simplified single-utterance speech engine.
 *
 * Design goals (vs. the previous segmented engine):
 *   - One SpeechSynthesisUtterance per step → smooth, continuous flow.
 *   - No artificial pauses, no [pause=NNN], no [break], no breath beats.
 *     Natural pacing comes from punctuation in the source text.
 *   - Emotion profiles still control rate + pitch (NOT pauses).
 *   - Inline `[em]…[/em]` / `[slow]…[/slow]` markup is stripped before
 *     speaking; consumers may still display the markup visually.
 *
 * Public API (unchanged):
 *   speak(text, onEnd?, emotion?)
 *   stop() / hardReset() / pause() / resume() / replay()
 *   isSupported() / isMuted() / setMuted() / toggleMute()
 *   getState() / subscribe()
 */

import { isDemoAllowed, isDemoModeEnabled } from './demoFlowService';

export type NarrationState = 'idle' | 'speaking' | 'paused' | 'muted';

type NarrationListener = (state: NarrationState) => void;

const KEEP_ALIVE_MS = 10_000;

// ─── Emotion profiles ─────────────────────────────────
// Emotion controls speech rate + pitch only. No pause manipulation.
export type DemoEmotion = 'CALM' | 'EXCITED' | 'EMPHASIS_HEAVY' | 'STORYTELLING' | 'ALERT';

export interface EmotionProfile {
  rate: number;
  pitch: number;
}

export const emotionProfiles: Record<DemoEmotion, EmotionProfile> = {
  CALM:           { rate: 0.95, pitch: 1.00 },
  EXCITED:        { rate: 1.05, pitch: 1.20 },
  EMPHASIS_HEAVY: { rate: 0.92, pitch: 1.12 },
  STORYTELLING:   { rate: 0.92, pitch: 1.05 },
  ALERT:          { rate: 1.05, pitch: 1.25 },
};

/** Volume applied to every utterance. */
export const speechConfig = {
  volume: 0.9,
};

// ─── Helpers ──────────────────────────────────────────

/**
 * Strip narration markup so the speech engine receives plain prose.
 * Pause-style tokens are removed; [em] and [slow] tags are flattened to
 * their inner text.
 */
export function cleanText(text: string): string {
  if (!text) return '';
  return text
    .replace(/\[pause=\d+\]/gi, ' ')
    .replace(/\[break\]/gi, ' ')
    .replace(/\[em\]([\s\S]*?)\[\/em\]/gi, '$1')
    .replace(/\[slow\]([\s\S]*?)\[\/slow\]/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

// ─── Service ──────────────────────────────────────────

class DemoNarrationService {
  private muted = false;
  private state: NarrationState = 'idle';
  private listeners = new Set<NarrationListener>();
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;
  private cachedVoice: SpeechSynthesisVoice | null = null;
  private voicesLoaded = false;
  private visibilityHandler: (() => void) | null = null;
  private speakSeq = 0;

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.loadVoices();
      window.speechSynthesis.onvoiceschanged = () => this.loadVoices();

      // Pause/resume on tab visibility change to avoid a wedged queue.
      this.visibilityHandler = () => {
        if (!('speechSynthesis' in window)) return;
        try {
          if (document.hidden) {
            window.speechSynthesis.pause();
          } else if (this.state === 'speaking') {
            window.speechSynthesis.resume();
          }
        } catch {
          /* ignore */
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  // ─── Public API ─────────────────────────────────────

  isSupported(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  /**
   * Hard reset the speech engine. Double-cancels with a 50 ms gap, which
   * is the documented workaround for Chrome's stuck-queue bug.
   */
  hardReset(): void {
    if (!this.isSupported()) return;
    try {
      window.speechSynthesis.pause();
      window.speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }, 50);
  }

  /**
   * Speak the given text as a single utterance. `onEnd` fires exactly
   * once when narration completes, errors, or hits the safety fallback.
   */
  speak(text: string, onEnd?: () => void, emotion: DemoEmotion = 'CALM'): void {
    const mySeq = ++this.speakSeq;
    this.cancelInternal();

    let ended = false;
    const finish = () => {
      if (ended) return;
      if (this.speakSeq !== mySeq) return;
      ended = true;
      this.clearTimers();
      this.setState(this.muted ? 'muted' : 'idle');
      onEnd?.();
    };

    const cleaned = cleanText(text);

    if (!isDemoModeEnabled() || !isDemoAllowed() || !this.isSupported() || this.muted || !cleaned) {
      queueMicrotask(finish);
      return;
    }

    // Hard reset BEFORE speaking — fixes "stuck queue" bug.
    this.hardReset();
    this.setState('speaking');

    const profile = emotionProfiles[emotion] ?? emotionProfiles.CALM;

    // Wait briefly so the deferred second cancel from hardReset() lands
    // before we enqueue the new utterance.
    setTimeout(() => {
      if (this.speakSeq !== mySeq) return;

      const utterance = new SpeechSynthesisUtterance(cleaned);
      utterance.volume = speechConfig.volume;
      utterance.rate = clamp(profile.rate, 0.5, 2.0);
      utterance.pitch = clamp(profile.pitch, 0.5, 2.0);

      if (!this.voicesLoaded) this.loadVoices();
      if (this.cachedVoice) utterance.voice = this.cachedVoice;

      utterance.onend = finish;
      utterance.onerror = (e) => {
        if (e?.error && e.error !== 'canceled' && e.error !== 'interrupted') {
          // eslint-disable-next-line no-console
          console.warn('[Demo] Speech error:', e.error);
        }
        finish();
      };

      try {
        window.speechSynthesis.speak(utterance);
      } catch {
        finish();
      }
    }, 80);

    // Safety fallback: even if SpeechSynthesis silently drops, advance.
    // Heuristic: 60 ms per character at default rate, scaled by profile.
    const fallbackMs = Math.max(3000, Math.round((cleaned.length * 60) / profile.rate));
    this.fallbackTimer = setTimeout(finish, fallbackMs);

    // Chrome keep-alive: works around the ~15 s silent-stop bug.
    this.keepAliveTimer = setInterval(() => {
      if (!this.isSupported()) return;
      try {
        if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        }
      } catch {
        /* ignore */
      }
    }, KEEP_ALIVE_MS);
  }

  stop(): void {
    this.speakSeq++;
    this.cancelInternal();
    this.setState(this.muted ? 'muted' : 'idle');
  }

  pause(): void {
    if (!this.isSupported()) return;
    try {
      window.speechSynthesis.pause();
    } catch {
      /* ignore */
    }
    this.setState('paused');
  }

  resume(): void {
    if (!this.isSupported()) return;
    try {
      window.speechSynthesis.resume();
    } catch {
      /* ignore */
    }
    this.setState('speaking');
  }

  replay(text: string, onEnd?: () => void, emotion: DemoEmotion = 'CALM'): void {
    this.speak(text, onEnd, emotion);
  }

  // ─── Mute ───────────────────────────────────────────

  isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) {
      this.cancelInternal();
      this.setState('muted');
    } else {
      this.setState('idle');
    }
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  // ─── State observation ──────────────────────────────

  getState(): NarrationState {
    return this.state;
  }

  subscribe(listener: NarrationListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ─── Internal ───────────────────────────────────────

  private loadVoices(): void {
    if (!this.isSupported()) return;
    const voices = window.speechSynthesis.getVoices();
    if (!voices?.length) return;
    this.voicesLoaded = true;
    this.cachedVoice =
      voices.find((v) => v.lang.startsWith('en') && v.name.includes('Google')) ??
      voices.find((v) => v.lang.startsWith('en-US')) ??
      voices.find((v) => v.lang.startsWith('en')) ??
      voices[0] ??
      null;
  }

  private cancelInternal(): void {
    this.clearTimers();
    if (this.isSupported()) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
  }

  private clearTimers(): void {
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  private setState(next: NarrationState): void {
    this.state = next;
    this.listeners.forEach((fn) => fn(next));
  }
}

export const demoNarration = new DemoNarrationService();
